import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@roger/ui-components';
import { Field } from '../FormFields';
import { sanitizePcrNumber } from './pcrNumber';

const PCR_LENGTH = 14;

const pcrWarning = (value: string): string | undefined => {
  if (value.length === 0 || value.length === PCR_LENGTH) {
    return undefined;
  }
  return 'Must be exactly 14 letters or numbers';
};

export type PreClaimReviewCardProps = {
  pcrNumber1: string;
  pcrNumber2: string;
  pcrComment: string;
  saving: boolean;
  onPcrNumber1Change: (value: string) => void;
  onPcrNumber2Change: (value: string) => void;
  onPcrCommentChange: (value: string) => void;
};

const PreClaimReviewCard: React.FC<PreClaimReviewCardProps> = ({
  pcrNumber1,
  pcrNumber2,
  pcrComment,
  saving,
  onPcrNumber1Change,
  onPcrNumber2Change,
  onPcrCommentChange,
}) => {
  const pcr1Warning = pcrWarning(pcrNumber1);
  const pcr2Warning = pcrWarning(pcrNumber2);

  return (
    <Card>
      <CardHeader className='pb-4'>
        <CardTitle className='text-sm font-semibold'>
          Pre-Claim Review
        </CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='space-y-1'>
          <Field
            label='PCR Tracking # (Billing Period 1)'
            htmlFor='episode_pcr_number_1'
            value={pcrNumber1}
            onChange={value =>
              onPcrNumber1Change(sanitizePcrNumber(String(value ?? '')))
            }
            warning={!!pcr1Warning}
            copyable={false}
            fieldSize='default'
            inputProps={{
              inputMode: 'text',
              pattern: '[A-Za-z0-9]*',
              autoCapitalize: 'characters',
              maxLength: PCR_LENGTH,
              disabled: saving,
            }}
          />
          {pcr1Warning && (
            <p className='text-sm text-orange-600'>{pcr1Warning}</p>
          )}
        </div>
        <div className='space-y-1'>
          <Field
            label='PCR Tracking # (Billing Period 2)'
            htmlFor='episode_pcr_number_2'
            value={pcrNumber2}
            onChange={value =>
              onPcrNumber2Change(sanitizePcrNumber(String(value ?? '')))
            }
            warning={!!pcr2Warning}
            copyable={false}
            fieldSize='default'
            inputProps={{
              inputMode: 'text',
              pattern: '[A-Za-z0-9]*',
              autoCapitalize: 'characters',
              maxLength: PCR_LENGTH,
              disabled: saving,
            }}
          />
          {pcr2Warning && (
            <p className='text-sm text-orange-600'>{pcr2Warning}</p>
          )}
        </div>
        <Field
          label='Comments'
          htmlFor='episode_pcr_comment'
          multiline
          value={pcrComment}
          onChange={onPcrCommentChange}
          copyable={false}
          fieldSize='default'
          textareaProps={{
            disabled: saving,
            className: 'min-h-[100px] resize-y',
          }}
        />
      </CardContent>
    </Card>
  );
};

export default PreClaimReviewCard;
